import { Navigate, Route, Routes } from "react-router-dom";
import PortalShell from "@/components/PortalShell";
import { LayoutDashboard, Store, Ticket, Users, Sparkles, Mail, FileText, CreditCard, LifeBuoy, UserCog, ShieldCheck, PiggyBank, Repeat, Bot, Radio, Gift, Scale, Pin, TrendingUp, MessageSquare, MapPin, BookOpen, Rocket, Gavel, Briefcase, Database, ClipboardList, PackageCheck, BadgeDollarSign, Link2, ScrollText, MailOpen, UsersRound } from "lucide-react";
import DatabaseMove from "@/pages/admin/DatabaseMove";
import IncompleteForms from "@/pages/admin/IncompleteForms";
import AdminOverview from "@/pages/admin/Overview";
import AdminStores from "@/pages/admin/Stores";
import AdminCoupons from "@/pages/admin/Coupons";
import AdminUsers from "@/pages/admin/Users";
import AdminOnboarding from "@/pages/admin/Onboarding";
import AdminDigestLog from "@/pages/admin/DigestLog";
import AdminInvoices from "@/pages/admin/Invoices";
import AdminSubscriptions from "@/pages/admin/Subscriptions";
import AdminPreLaunch from "@/pages/admin/PreLaunch";
import AdminLegal from "@/pages/admin/Legal";
import AdminSupportTickets from "@/pages/admin/SupportTickets";
import AdminMerchants from "@/pages/admin/Merchants";
import AdminAuditLogs from "@/pages/admin/AuditLogs";
import AdminCashback from "@/pages/admin/Cashback";
import AdminAISettings from "@/pages/admin/AISettings";
import AdminOps from "@/pages/admin/Ops";
import AdminRewards from "@/pages/admin/Rewards";
import GiftCardFulfilment from "@/pages/admin/GiftCardFulfilment";
import AdminReconciliation from "@/pages/admin/Reconciliation";
import AdminStorefrontOverrides from "@/pages/admin/StorefrontOverrides";
import AdminMarketingAnalytics from "@/pages/admin/MarketingAnalytics";
import AdminSmsConsole from "@/pages/admin/SmsConsole";
import CrmDemoLeads from "@/pages/admin/CrmDemoLeads";
import ConversionIntegrity from "@/pages/admin/ConversionIntegrity";
import AdminEmailAnalytics from "@/pages/admin/EmailAnalytics";
import AdminCheckoutVerifications from "@/pages/admin/CheckoutVerifications";
import AdminDiscoveryLocationSettings from "@/pages/admin/DiscoveryLocationSettings";
import AdminSupportKnowledge from "@/pages/admin/SupportKnowledge";
import AdminPricing from "@/pages/admin/Pricing";
import AdminSponsoredCashback from "@/pages/admin/SponsoredCashback";
import TeamList from "@/pages/admin/team/TeamList";
import TeamMemberDetail from "@/pages/admin/team/TeamMemberDetail";
import { useRefundQueue } from "@/components/admin/sponsored/useRefundQueue";
import { useAuth } from "@/context/AuthContext";
import { DELEGATED_ROLES } from "@/lib/team";

// `perm` = the Cyannect team permission a Finance / Operations / Support member needs to see the page (server-enforced too).
const links = [
  { to: "/admin", end: true, label: "Overview", icon: LayoutDashboard, testid: "admin-nav-overview", perm: "reports.view" },
  { to: "/admin/ops", label: "Ops Console", icon: Radio, testid: "admin-nav-ops" },
  { to: "/admin/reconciliation", label: "Reconciliation", icon: Scale, testid: "admin-nav-reconciliation", perm: "finance.view" },
  { to: "/admin/marketing", label: "Ad analytics", icon: TrendingUp, testid: "admin-nav-marketing" },
  { to: "/admin/sms", label: "SMS console", icon: MessageSquare, testid: "admin-nav-sms" },
  { to: "/admin/storefront-overrides", label: "Storefront pins", icon: Pin, testid: "admin-nav-overrides" },
  { to: "/admin/checkout-verifications", label: "Checkout verifications", icon: ShieldCheck, testid: "admin-nav-checkout-verifications", badge: "New", perm: "campaign.view" },
  { to: "/admin/onboarding", label: "Brand onboarding", icon: Sparkles, testid: "admin-nav-onboarding", perm: "merchant.view" },
  { to: "/admin/merchants", label: "Merchants", icon: UserCog, testid: "admin-nav-merchants", perm: "merchant.view" },
  { to: "/admin/subscriptions", label: "Subscriptions", icon: CreditCard, testid: "admin-nav-subscriptions", perm: "finance.view" },
  { to: "/admin/pricing", label: "Pricing", icon: BadgeDollarSign, testid: "admin-nav-pricing", badge: "New", perm: "pricing.view" },
  { to: "/admin/pre-launch", label: "Pre-launch billing", icon: Rocket, testid: "admin-nav-pre-launch" },
  { to: "/admin/terms", label: "Terms & Conditions", icon: Gavel, testid: "admin-nav-legal" },
  { to: "/admin/invoices", label: "Invoices", icon: FileText, testid: "admin-nav-invoices", perm: "finance.view" },
  { to: "/admin/support", label: "Support", icon: LifeBuoy, testid: "admin-nav-support", perm: "support.view" },
  { to: "/admin/support-knowledge", label: "Support knowledge", icon: BookOpen, testid: "admin-nav-support-knowledge", perm: "support.view" },
  { to: "/admin/cashback", label: "Cashback", icon: PiggyBank, testid: "admin-nav-cashback", perm: "finance.view" },
  { to: "/admin/sponsored-cashback", label: "Sponsored Cashback", icon: Repeat, testid: "admin-nav-sponsored-cashback", badge: "Preview", perm: "sponsored_cashback.view" },
  { to: "/admin/rewards", label: "Rewards", icon: Gift, testid: "admin-nav-rewards", end: true, perm: "gift_cards.view" },
  { to: "/admin/rewards/fulfilment", label: "Gift card fulfilment", icon: PackageCheck, testid: "admin-nav-gift-card-fulfilment", badge: "New", perm: "gift_cards.view" },
  { to: "/admin/ai-model", label: "AI model", icon: Bot, testid: "admin-nav-ai" },
  { to: "/admin/discovery-geo", label: "Discovery · Location", icon: MapPin, testid: "admin-nav-discovery-geo" },
  { to: "/admin/stores", label: "Stores", icon: Store, testid: "admin-nav-stores", perm: "merchant.view" },
  { to: "/admin/coupons", label: "Coupons", icon: Ticket, testid: "admin-nav-coupons", perm: "campaign.view" },
  { to: "/admin/users", label: "Users", icon: Users, testid: "admin-nav-users" },
  { to: "/admin/crm", label: "CRM – Demo leads", icon: Briefcase, testid: "admin-nav-crm" },
  { to: "/admin/conversion-integrity", label: "Conversion Integrity", icon: Link2, testid: "admin-nav-conversion-integrity", perm: "merchant.view" },
  { to: "/admin/audit-log", label: "Audit log", icon: ScrollText, testid: "admin-nav-audit" },
  { to: "/admin/digest-log", label: "Digest log", icon: Mail, testid: "admin-nav-digest-log" },
  { to: "/admin/emails", label: "Email analytics", icon: MailOpen, testid: "admin-nav-emails" },
  { to: "/admin/incomplete-forms", label: "Incomplete forms", icon: ClipboardList, testid: "admin-nav-incomplete-forms" },
  { to: "/admin/team", label: "Team Management", icon: UsersRound, testid: "admin-nav-team", superAdmin: true, section: "Settings" },
  { to: "/admin/database-move", label: "Move database", icon: Database, testid: "admin-nav-database-move", superAdmin: true },
];

export default function AdminPortal() {
  const { user } = useAuth();
  const sa = user?.role === "super_admin";
  const delegated = DELEGATED_ROLES.includes(user?.role);
  const perms = user?.team_permissions || [];
  const queue = useRefundQueue(!delegated || perms.includes("sponsored_cashback.view"));
  const navLinks = links.filter((l) => (delegated ? perms.includes(l.perm) : sa || !l.superAdmin))
    .map((l) => (l.to === "/admin/sponsored-cashback" ? { ...l, count: queue?.open || 0 } : l));
  const home = delegated && !perms.includes("reports.view")
    ? <Navigate to={navLinks[0]?.to || "/admin/login"} replace /> : <AdminOverview />;
  return (
    <PortalShell title="Admin" subtitle="Console" links={navLinks} testid="admin-portal">
      <Routes>
        <Route index element={home} />
        <Route path="ops" element={<AdminOps />} />
        <Route path="reconciliation" element={<AdminReconciliation />} />
        <Route path="marketing" element={<AdminMarketingAnalytics />} />
        <Route path="sms" element={<AdminSmsConsole />} />
        <Route path="crm" element={<CrmDemoLeads />} />
        <Route path="conversion-integrity" element={<ConversionIntegrity />} />
        <Route path="storefront-overrides" element={<AdminStorefrontOverrides />} />
        <Route path="checkout-verifications" element={<AdminCheckoutVerifications />} />
        <Route path="onboarding" element={<AdminOnboarding />} />
        <Route path="merchants" element={<AdminMerchants />} />
        <Route path="subscriptions" element={<AdminSubscriptions />} />
        <Route path="pricing" element={<AdminPricing />} />
        <Route path="pre-launch" element={<AdminPreLaunch />} />
        <Route path="terms" element={<AdminLegal />} />
        <Route path="legal" element={<Navigate to="/admin/terms" replace />} />
        <Route path="invoices" element={<AdminInvoices />} />
        <Route path="support" element={<AdminSupportTickets />} />
        <Route path="support-knowledge" element={<AdminSupportKnowledge />} />
        <Route path="cashback" element={<AdminCashback />} />
        <Route path="sponsored-cashback" element={<AdminSponsoredCashback />} />
        <Route path="rewards" element={<AdminRewards />} />
        <Route path="rewards/fulfilment" element={<GiftCardFulfilment />} />
        <Route path="ai-model" element={<AdminAISettings />} />
        <Route path="discovery-geo" element={<AdminDiscoveryLocationSettings />} />
        <Route path="stores" element={<AdminStores />} />
        <Route path="coupons" element={<AdminCoupons />} />
        <Route path="users" element={<AdminUsers />} />
        <Route path="audit-log" element={<AdminAuditLogs />} />
        <Route path="digest-log" element={<AdminDigestLog />} />
        <Route path="emails" element={<AdminEmailAnalytics />} />
        <Route path="team" element={<TeamList />} />
        <Route path="team/:id" element={<TeamMemberDetail />} />
        <Route path="database-move" element={<DatabaseMove />} />
        <Route path="incomplete-forms" element={<IncompleteForms />} />
      </Routes>
    </PortalShell>
  );
}
